import { css, html, LitElement, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { styleMap } from "lit/directives/style-map.js";
import { Temporal } from "temporal-polyfill";
import { fetchCalendarCounts } from "./read-path.ts";
import { chevronLeftIcon, chevronRightIcon } from "./icons.ts";
import { monthGrid, volumeScale, WEEKDAY_INITIALS } from "./calendar.ts";
import { DEFAULT_REGION_SLUG, EARLIEST_OBSERVATION_DATE, observationToday, regionBySlug } from "./constants.ts";

const MONTH_LABEL = {month: 'long', year: 'numeric'} as const;
const DAY_LABEL = {weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'} as const;

/**
 * Diameter of a full-volume circle, as a percentage of the day cell. Held short
 * of the cell so even the busiest days keep a gap between neighbours — in peak
 * season most days sit near the top of the domain, and without that gap a summer
 * month reads as one solid mass rather than as a row of days.
 */
const FULL_VOLUME_DIAMETER = 72;

type Counts = Map<string, number>;

/** Wraps a step arrow's path in the icon set's shared viewBox. */
export const stepIcon = (path: unknown) =>
  html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" aria-hidden="true">${path}</svg>`;

/**
 * The date picker's arrow buttons — month steppers here, day steppers in
 * obs-panel. Lit styles are scoped per component, so the rule lives here as a
 * CSSResult both put in their `static styles` (the profile-shared convention).
 *
 * Quiet chrome rather than a form control: the arrows sit inside the picker and
 * step the label between them, so boxing them like the "Go to…" select would
 * overstate them and give the picker three competing button treatments.
 */
export const stepButtonStyles = css`
  .step {
    align-items: center;
    background: none;
    border: 0;
    border-radius: 4px;
    color: var(--color-text-muted);
    cursor: pointer;
    display: flex;
    flex: 0 0 auto;
    height: 1.75rem;
    justify-content: center;
    padding: 0;
    width: 1.75rem;
  }
  .step svg {
    display: block;
    fill: currentColor;
    height: 1.25rem;
    width: 1.25rem;
  }
  .step:hover:not(:disabled) {
    background: var(--color-surface-subtle);
    color: var(--color-text);
  }
  .step:disabled {
    color: var(--color-text-disabled);
    cursor: default;
  }
  .step:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 1px;
  }
`;

/**
 * A month calendar for picking the observation date, where each day carries a
 * circle sized by that day's sighting volume — so the shape of the season is
 * visible while choosing, and quiet days are obvious before they're clicked.
 *
 * Emits `date-selected` (bubbling, composed) with an ISO date string, the same
 * event the day-stepping buttons and the sighting form use.
 */
@customElement('date-calendar')
export class DateCalendar extends LitElement {
  static styles = [stepButtonStyles, css`
    :host {
      display: block;
      user-select: none;
    }

    /* ---- month heading -------------------------------------------------- */

    /* Same 21rem measure and gap as the grid and the day-stepping row below it,
       so all three arrows sit on the same two vertical lines. */
    .month-nav {
      align-items: center;
      display: flex;
      gap: 0.25rem;
      justify-content: center;
      margin: 0 auto;
      max-width: 21rem;
    }
    h3 {
      color: var(--color-text);
      flex-grow: 1;
      font-size: var(--fs-body1);
      font-weight: 400;
      margin: 0;
      text-align: center;
    }

    /* ---- grid ----------------------------------------------------------- */

    /* Column headers, cell type and the blue volume ramp are the profile pages'
       presence table (profile-shared.ts) restated for a day grid — the site
       already has one way of showing activity over time. */
    .weekdays,
    .grid {
      display: grid;
      font-variant-numeric: tabular-nums;
      grid-template-columns: repeat(7, 1fr);
      margin: 0 auto;
      max-width: 21rem;
      width: 100%;
    }
    .weekdays {
      color: var(--color-text-muted);
      font-size: var(--fs-caption);
      font-weight: 400;
      padding: 0.25rem 0 0.125rem;
      text-align: center;
    }

    .day {
      align-items: center;
      aspect-ratio: 1;
      background: none;
      border: 0;
      border-radius: 50%;
      color: var(--color-text);
      cursor: pointer;
      display: flex;
      font-family: inherit;
      font-size: var(--fs-body2);
      justify-content: center;
      padding: 0;
      position: relative;
      width: 100%;
    }

    /* The encoding: one hue, constant opacity, size alone carrying volume. A data
       colour, though it takes the interface's teal, as the profile pages' presence
       ramp does. */
    .volume {
      aspect-ratio: 1;
      background: var(--color-primary);
      border-radius: 50%;
      left: 50%;
      opacity: 0.32;
      position: absolute;
      top: 50%;
      translate: -50% -50%;
    }

    .numeral {
      position: relative; /* above the circle */
    }

    /* Days spilling in from the neighbouring months: present and clickable, but
       clearly not part of this month. */
    .outside {
      color: var(--color-text-muted);
    }
    .outside .volume {
      opacity: 0.16;
    }
    .day:disabled {
      color: var(--color-text-disabled);
      cursor: default;
    }

    /* ---- states --------------------------------------------------------- */

    .day:hover:not(:disabled) {
      box-shadow: inset 0 0 0 1px var(--color-border-strong);
    }
    .today .numeral {
      font-weight: 700;
    }
    .selected {
      box-shadow: inset 0 0 0 2px var(--color-primary);
      color: var(--color-primary-hover);
    }
    .selected .numeral {
      font-weight: 700;
    }
    .day:hover.selected {
      box-shadow: inset 0 0 0 2px var(--color-primary-hover);
    }
    .day:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 1px;
    }
  `];

  /** The selected date, ISO `YYYY-MM-DD`. */
  @property({type: String})
  date!: string;

  /**
   * The active region. Day counts are scoped to it, so that a day's circle
   * counts exactly the sightings the map will draw when you click it.
   */
  @property({type: String})
  regionSlug: string = DEFAULT_REGION_SLUG;

  /** The month on screen. Follows `date` unless the user has paged away from it. */
  @state()
  private month: Temporal.PlainYearMonth = observationToday().toPlainYearMonth();

  /** Day (`YYYY-MM-DD`) → sighting count, for every month fetched so far. */
  @state()
  private counts: Counts = new Map();

  /** Months already requested, so paging back and forth doesn't refetch. */
  #fetched = new Set<string>();

  /**
   * Bumped by {@link refresh}. A request that was in flight when the counts were
   * invalidated carries the old generation and is dropped on arrival — without
   * this it would resolve after the refetch it raced and write its stale counts
   * back over the fresh ones.
   */
  #generation = 0;

  /** Day an arrow key moved to, awaiting focus once it has been rendered. */
  #pendingFocus: string | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    if (this.date)
      this.month = Temporal.PlainDate.from(this.date).toPlainYearMonth();
  }

  protected willUpdate(changed: PropertyValues<this>): void {
    // Counts are scoped to the region, so a region change invalidates every
    // month cached under the old one.
    //
    // This has to happen HERE, driven by the property actually arriving, rather
    // than by the app calling refresh() when it changes the region. That call
    // lands before Lit has propagated the new slug, so the refetch it triggers
    // would run against the old region, cache it under this month, and then
    // #fetched would suppress the correct request when the slug finally showed
    // up — leaving circles quietly scoped to the region you just left.
    if (changed.has('regionSlug') && changed.get('regionSlug') !== undefined)
      this.refresh();
    // Follow the selection when it lands outside the month on screen — a day
    // step across a boundary, or a jump to an occurrence from another season.
    if (changed.has('date') && this.date) {
      const selectedMonth = Temporal.PlainDate.from(this.date).toPlainYearMonth();
      if (!selectedMonth.equals(this.month))
        this.month = selectedMonth;
    }
  }

  protected updated(): void {
    this.fetchCounts(this.month);
    // An arrow key sends the new date up to the app and waits for it to come
    // back down; only once it has is the cell to focus the one under the
    // cursor. Focusing any earlier lands on a node the next render relabels.
    if (this.#pendingFocus && this.#pendingFocus === this.date) {
      const cell = this.renderRoot.querySelector<HTMLButtonElement>(`[data-date="${this.#pendingFocus}"]`);
      this.#pendingFocus = null;
      cell?.focus();
    }
  }

  /**
   * Fetch the month on screen again, keeping its circles until the new counts
   * land — for a new read-path build, which arrives a few times an hour and
   * shouldn't make every circle blink. Other cached months are dropped, to be
   * fetched afresh when paged back to.
   */
  revalidate(): void {
    this.#generation++;
    this.#fetched.clear();
    this.fetchCounts(this.month);
  }

  /** Drop cached counts and refetch, e.g. after the user saves a sighting. */
  refresh(): void {
    this.#generation++;
    this.#fetched.clear();
    this.counts = new Map();
    this.fetchCounts(this.month);
  }

  protected render() {
    // Read once per render, not once per module load, so a tab open across
    // local midnight starts offering the new day.
    const today = observationToday();
    const selected = this.date ? Temporal.PlainDate.from(this.date) : null;
    const days = monthGrid(this.month);
    const firstOfMonth = this.month.toPlainDate({day: 1});
    const prevDisabled = Temporal.PlainYearMonth.compare(this.month, EARLIEST_OBSERVATION_DATE.toPlainYearMonth()) <= 0;
    const nextDisabled = Temporal.PlainYearMonth.compare(this.month, today.toPlainYearMonth()) >= 0;
    // Roving tabindex: one tab stop for the whole grid, on the selected day. If
    // the user has paged away from it, the 1st of the month takes the stop —
    // otherwise no cell would be tabbable and the grid would be unreachable.
    const tabbable = selected && days.some(d => d.equals(selected)) ? selected : firstOfMonth;

    return html`
      <div class="month-nav">
        <button class="step" @click=${() => this.pageMonth(-1)} ?disabled=${prevDisabled} type="button" aria-label="Previous month">${stepIcon(chevronLeftIcon)}</button>
        <h3 aria-live="polite">${firstOfMonth.toLocaleString(undefined, MONTH_LABEL)}</h3>
        <button class="step" @click=${() => this.pageMonth(1)} ?disabled=${nextDisabled} type="button" aria-label="Next month">${stepIcon(chevronRightIcon)}</button>
      </div>
      <div class="weekdays" aria-hidden="true">
        ${WEEKDAY_INITIALS.map(d => html`<span>${d}</span>`)}
      </div>
      <div class="grid" role="group" aria-label="Observation date" @keydown=${this.onKeydown}>
        ${days.map(day => this.renderDay(day, selected, tabbable, today))}
      </div>
    `;
  }

  private renderDay(
    day: Temporal.PlainDate,
    selected: Temporal.PlainDate | null,
    tabbable: Temporal.PlainDate,
    today: Temporal.PlainDate,
  ) {
    const iso = day.toString();
    const count = this.counts.get(iso) ?? 0;
    const disabled = Temporal.PlainDate.compare(day, today) > 0
      || Temporal.PlainDate.compare(day, EARLIEST_OBSERVATION_DATE) < 0;

    const written = day.toLocaleString(undefined, DAY_LABEL);
    const label = disabled
      ? written
      : `${written}, ${count === 1 ? '1 sighting' : `${count} sightings`}`;

    const scale = volumeScale(count);

    return html`
      <button
        class=${classMap({
          day: true,
          outside: day.month !== this.month.month,
          selected: !!selected && day.equals(selected),
          today: day.equals(today),
        })}
        type="button"
        data-date=${iso}
        ?disabled=${disabled}
        tabindex=${day.equals(tabbable) ? 0 : -1}
        aria-label=${label}
        title=${label}
        aria-current=${!!selected && day.equals(selected) ? 'date' : 'false'}
        @click=${() => this.selectDate(iso)}
      >
        ${scale > 0 ? html`<span class="volume" style=${styleMap({width: `${scale * FULL_VOLUME_DIAMETER}%`})}></span>` : ''}
        <span class="numeral">${day.day}</span>
      </button>
    `;
  }

  private selectDate(iso: string) {
    this.dispatchEvent(new CustomEvent('date-selected', {bubbles: true, composed: true, detail: iso}));
  }

  private pageMonth(delta: number) {
    this.month = delta < 0 ? this.month.subtract({months: 1}) : this.month.add({months: 1});
  }

  /**
   * Arrow-key movement across the grid. Only one day is tabbable (roving
   * tabindex), so the calendar costs one tab stop rather than 42.
   */
  private onKeydown(e: KeyboardEvent) {
    const steps: Record<string, number> = {
      ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7,
    };
    const step = steps[e.key];
    if (step === undefined)
      return;
    // Move from the cell under the cursor, which after paging months is not
    // necessarily the selected day.
    const from = (e.target as HTMLElement | null)?.getAttribute?.('data-date') ?? this.date;
    if (!from)
      return;
    const target = Temporal.PlainDate.from(from).add({days: step});
    if (Temporal.PlainDate.compare(target, observationToday()) > 0
      || Temporal.PlainDate.compare(target, EARLIEST_OBSERVATION_DATE) < 0)
      return;
    e.preventDefault();
    this.#pendingFocus = target.toString();
    this.selectDate(target.toString());
  }

  private async fetchCounts(month: Temporal.PlainYearMonth) {
    const generation = this.#generation;
    const key = month.toString();
    if (this.#fetched.has(key))
      return;
    this.#fetched.add(key);

    // The grid shows the tail of the previous month and the head of the next,
    // so fetch what's drawn, not just the month proper.
    const days = monthGrid(month);
    const from = days[0]!.toString();
    const to = days[days.length - 1]!.toString();

    let counts: Counts;
    try {
      // A signed-in tab's own new sighting reaches the counts with the next build, a
      // minute or so: the write API overlays only the list, which the counts can't
      // tell apart from the file's (decision 065).
      counts = await fetchCalendarCounts(from, to, regionBySlug(this.regionSlug).slug);
    } catch (error) {
      if (generation !== this.#generation) return;
      this.#fetched.delete(key);
      console.error('Failed to load sighting volume', error);
      return;
    }
    if (generation !== this.#generation) return;
    // Replace the grid's range whole, so a day that has dropped to zero loses
    // its circle rather than keeping the one it had.
    const merged = new Map([...this.counts].filter(([day]) => day < from || day > to));
    for (const [day, count] of counts) merged.set(day, count);
    this.counts = merged;
  }
}
